import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Button from '../../uikit/button';
import DropdownMenu from '../../uikit/dropdown_menu';
import Input from '../../uikit/input';
// import Select from '../../uikit/select';
import InfiniteScrollDropdown from '../../uikit/infinite_scroll_dropdown';
import TopActionBar from '../../uikit/top_actionbar';
import { getOneMaterialsIndent } from '../../utils/apiUtils/common';
import {
  getUnusedDriverVehicles,
  inventoryDetailsByParentsInventoryId,
  inventoryDetailsParentsByInventoryTypeId,
  newLogistic,
} from '../../utils/apiUtils/inventoryUtils';
import { getDateAndTimeModifiedFormat } from '../../utils/commonUtils';
import useLogisticsDropdowns from './LogisticsDropdownUtil';
import MaterialSelector from '../MaterialSelector';

export default function LogisticsCreateView() {
  const { pID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [vehiclesList, setVehiclesList] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [inventoryDetailsList, setInventoryDetailsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vehicleDriverSelectionMode, setVehicleDriverSelectionMode] =
    useState('existing');

  // Form states
  const [fromSite, setFromSite] = useState(null);
  const [toSite, setToSite] = useState(null);
  const [historicalCheck, setHistoricalCheck] = useState(false);
  const [selectedStart, setSelectedStart] = useState(new Date());
  const [selectedEnd, setSelectedEnd] = useState(new Date());
  const [selectedVehicleOption, setSelectedVehicleOption] = useState('');
  const [selectedDriverOption, setSelectedDriverOption] = useState('');
  const [selectedVehDriverList, setSelectedVehDriverList] = useState([]);
  const [customVehicleName, setCustomVehicleName] = useState('');
  const [customDriverName, setCustomDriverName] = useState('');
  const [materialsRequested, setMaterialsRequested] = useState([
    {
      inventoryDetailsId: '',
      quantity: 0,
      category: null,
      subCatId: '',
    },
  ]);
  const [miIDs, setMiIDs] = useState([]);
  const [miInput, setMiInput] = useState('');

  const isFromProjectRoute = location.pathname.includes('project');

  const {
    fetchWarehouses,
    fetchCategories,
    handleFetchError,
    transformWarehouseOption,
    transformCategoryOption,
  } = useLogisticsDropdowns();

  const fetchFromWarehouses = async (params) => {
    const resp = await fetchWarehouses(params);
    if (toSite?.value) {
      resp.content = resp.content.filter((it) => it.roomId !== toSite.value);
    }
    return resp;
  };

  const fetchToWarehouses = async (params) => {
    const resp = await fetchWarehouses(params);
    if (fromSite?.value) {
      resp.content = resp.content.filter((it) => it.roomId !== fromSite.value);
    }
    return resp;
  };

  const fetchUnusedDriverVeh = async () => {
    setLoading(true);
    try {
      const resp = await getUnusedDriverVehicles(
        encodeURIComponent(
          getDateAndTimeModifiedFormat(selectedStart || new Date())
        ),
        encodeURIComponent(
          getDateAndTimeModifiedFormat(selectedEnd || new Date())
        )
      );
      setDriversList(resp.drivers || []);
      setVehiclesList(resp.vehicles || []);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const getInitialData = () => {
    fetchUnusedDriverVeh();
  };

  const onAddVehicleDriver = () => {
    if (!selectedVehicleOption || !selectedDriverOption) return;
    const obj = {
      start: selectedStart,
      end: selectedEnd,
      driver: driversList[selectedDriverOption],
      vehicle: vehiclesList[selectedVehicleOption],
    };
    setSelectedVehDriverList([...selectedVehDriverList, obj]);
    setSelectedVehDriverList([obj]);
    setSelectedVehicleOption('');
    setSelectedDriverOption('');
    setSelectedStart(new Date());
    setSelectedEnd(new Date());
    setCustomVehicleName('');
    setCustomDriverName('');
  };

  const onAddCustomVehicleDriver = () => {
    if (!customDriverName || !customVehicleName) return;
    const obj = {
      isCustom: true,
      driver: customDriverName,
      vehicle: customVehicleName,
    };
    setSelectedVehDriverList([obj]);
    setCustomVehicleName('');
    setCustomDriverName('');
    setSelectedVehicleOption('');
    setSelectedDriverOption('');
  };

  const onRemoveVehicleDriver = (key, index) => {
    const newArr = [...selectedVehDriverList];
    newArr.splice(index, 1);
    setSelectedVehDriverList(newArr);
  };

  const onCategoryChange = async (cat, idx) => {
    if (!cat) {
      const newArr = [...materialsRequested];
      newArr[idx] = {
        ...newArr[idx],
        category: null,
        subCatId: '',
        inventoryDetailsId: '',
        measurementUnit: '',
        quantity: 0,
      };
      setMaterialsRequested(newArr);
      return;
    }
    const newArr = [...materialsRequested];
    newArr[idx] = {
      ...newArr[idx],
      category: cat,
      subCatId: '',
      inventoryDetailsId: '',
      measurementUnit: '',
      quantity: 0,
    };
    setMaterialsRequested(newArr);
    try {
      const resp = await inventoryDetailsParentsByInventoryTypeId(cat?.value);
      const newSubcat = [...subCategories];
      newSubcat[idx] = resp || [];
      setSubCategories(newSubcat);
    } catch (e) {
      console.error(e);
    }
  };

  const onSubCategoryChange = async (e, idx) => {
    const newArr = [...materialsRequested];
    materialsRequested[idx].subCatId = e.target.value;
    materialsRequested[idx].inventoryDetailsId = '';
    materialsRequested[idx].measurementUnit = '';
    setMaterialsRequested(newArr);
    try {
      const resp = await inventoryDetailsByParentsInventoryId(e.target.value);
      const newInventoryDetails = [...inventoryDetailsList];
      newInventoryDetails[idx] = resp.body || resp || [];
      setInventoryDetailsList(newInventoryDetails);
    } catch (e) {
      console.error(e);
    }
  };

  const onSaveClick = async () => {
    setLoading(true);
    try {
      if (toSite?.value === fromSite?.value) {
        throw new Error('From and To location can not be same');
      }
      const validMaterials =
        materialsRequested?.filter(
          (it) => it.inventoryDetailsId && Number(it.quantity) > 0
        ) || [];

      if (!fromSite || !toSite || validMaterials.length === 0) {
        throw new Error(
          'Please select locations and ensure at least one item has a material and quantity greater than 0'
        );
      }
      const obj = {
        fromSiteId: fromSite?.value,
        toSiteId: toSite?.value,
        indentedMaterialQuantityDtoList: validMaterials.map((it) => ({
          inventoryDetailsId: it.inventoryDetailsId,
          quantity: it.quantity,
        })),
        materialIndentIds: miIDs?.map((it) => it.value),
        driverVehicleDatesDtos: selectedVehDriverList
          ?.filter((it) => !it?.isCustom)
          ?.map((it) => ({
            start: getDateAndTimeModifiedFormat(it.start),
            end: getDateAndTimeModifiedFormat(it.end),
            driverId: it.driver?.uid,
            vehicleId: it?.vehicle?.vehicleId,
          })),
        historical: historicalCheck,
        driversWildCard: selectedVehDriverList
          ?.filter((it) => it?.isCustom)
          ?.map((it) => it?.driver)
          .join(','),
        vehiclesWildCard: selectedVehDriverList
          ?.filter((it) => it?.isCustom)
          ?.map((it) => it?.vehicle)
          .join(','),
      };

      const resp = await newLogistic(obj);
      window.triggerAlert({
        title: 'Success',
        type: 'success',
        message: 'Logistics created successfully',
      });
      navigate(
        isFromProjectRoute
          ? `/project/${pID}/logistics/${resp.logisticsId}/view`
          : `/admin/logistics/details/${resp.logisticsId}`
      );
    } catch (e) {
      console.error(e);

      const message =
        e?.response?.data?.message ||
        e?.message ||
        'Something went wrong. Please try again.';

      window.triggerAlert({
        title: 'Error',
        type: 'error',
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const addMiId = async (miId) => {
    const trimmed = String(miId).trim();
    if (!trimmed) return;
    if (miIDs.some((it) => String(it.value) === trimmed)) return;
    setMiIDs((prev) => [...prev, { value: trimmed }]);
    try {
      const resp = await getOneMaterialsIndent(trimmed);
      const newMaterialItems = [];
      const newSubCats = [];
      const newItems = [];
      resp?.indentedMaterialQuantities?.forEach((it) => {
        if (it.decision === 'LOGISTIC' && it.poLogisticId === null) {
          newMaterialItems.push({
            indentedMaterialQuantityId: it.indentedMaterialQuantityId,
            miId: trimmed,
            inventoryDetailsId: it.inventoryDetails?.inventoryDetailsId,
            quantity: it.quantity,
            category: {
              value:
                it?.inventoryDetails?.inventoryDetailsParent?.inventoryType
                  ?.inventoryTypeId,
              label:
                it?.inventoryDetails?.inventoryDetailsParent?.inventoryType
                  ?.inventoryType,
            },
            subCatId:
              it?.inventoryDetails?.inventoryDetailsParent
                ?.inventoryDetailsParentId,
          });
          newSubCats.push([
            { ...it?.inventoryDetails?.inventoryDetailsParent, miId: trimmed },
          ]);
          newItems.push([{ ...it?.inventoryDetails, miId: trimmed }]);
        }
      });
      if (newMaterialItems.length === 0) {
        setMiIDs((prev) => prev.filter((it) => String(it.value) !== trimmed));
        window?.triggerAlert?.({
          title: 'No eligible items',
          type: 'error',
          message: 'No LOGISTIC items found for this MI',
        });
        return;
      }
      const isEmptyInitial =
        materialsRequested.length === 1 &&
        !materialsRequested[0].inventoryDetailsId &&
        !materialsRequested[0].subCatId;

      if (isEmptyInitial) {
        setMaterialsRequested(newMaterialItems);
        setSubCategories(newSubCats);
        setInventoryDetailsList(newItems);
      } else {
        setMaterialsRequested((prev) => [...newMaterialItems, ...prev]);
        setSubCategories((prev) => [...newSubCats, ...prev]);
        setInventoryDetailsList((prev) => [...newItems, ...prev]);
      }
      if (resp?.toRoom?.roomId && resp?.toRoom?.roomName) {
        setToSite({
          value: resp.toRoom.roomId,
          label: resp.toRoom.roomName,
        });
      }
    } catch (e) {
      setMiIDs((prev) => prev.filter((it) => String(it.value) !== trimmed));
      window?.triggerAlert?.({
        title: 'Error',
        type: 'error',
        message: 'Entered MI ID does not exist',
      });
    }
  };

  const removeMiId = (miId) => {
    const trimmed = String(miId).trim();
    setMiIDs((prev) => prev.filter((it) => String(it.value) !== trimmed));
    setMaterialsRequested((prev) => {
      const next = prev.filter((it) => it.miId !== trimmed);
      if (next.length === 0) {
        return [
          {
            inventoryDetailsId: '',
            quantity: 0,
            category: null,
            subCatId: '',
          },
        ];
      }
      return next;
    });
    setSubCategories((prev) => {
      const next = prev.filter((it) => it?.[0]?.miId !== trimmed);
      return next;
    });
    setInventoryDetailsList((prev) => {
      const next = prev.filter((it) => it?.[0]?.miId !== trimmed);
      return next;
    });
  };

  const onMiInputKeyDown = async (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = (e.target.value || '').trim();
      if (val) {
        await addMiId(val);
        setMiInput('');
      }
    }
  };

  const onInventorySelect = (e, idx) => {
    const newArr = [...materialsRequested];
    materialsRequested[idx].inventoryDetailsId = e.target.value;
    materialsRequested[idx].measurementUnit = inventoryDetailsList?.[idx]?.find(
      (it) => it?.inventoryDetailsId == e.target.value
    )?.measurementUnit;
    setMaterialsRequested(newArr);
  };

  const onQuantityUpdate = (e, idx) => {
    const newArr = [...materialsRequested];
    materialsRequested[idx].quantity = e.target.value;
    setMaterialsRequested(newArr);
  };

  const onAddRow = () => {
    const newArr = [...materialsRequested];
    newArr.push({
      inventoryDetailsId: '',
      quantity: 0,
      category: null,
      subCatId: '',
    });
    setMaterialsRequested(newArr);
  };

  const onDeleteRow = (index) => {
    const newArr = [...materialsRequested];
    newArr.splice(index, 1);
    setMaterialsRequested(newArr);
    const newSubCatArr = [...subCategories];
    newSubCatArr.splice(index, 1);
    setSubCategories(newSubCatArr);
    const newItemsArr = [...inventoryDetailsList];
    newItemsArr.splice(index, 1);
    setInventoryDetailsList(newItemsArr);
  };

  const onStartChange = (date) => {
    setSelectedStart(date);
    setSelectedEnd(date);
    if (selectedStart && selectedEnd) {
      fetchUnusedDriverVeh();
    }
  };

  const onEndChange = (date) => {
    setSelectedEnd(date);
    if (selectedStart && selectedEnd) {
      fetchUnusedDriverVeh();
    }
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TopActionBar
        containerClassName="px-4"
        leftContent={
          <div className="flex items-center justify-center gap-4">
            <Button
              containerClass="md:block hidden"
              onClick={() => navigate(-1)}
              startIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="6"
                  height="11"
                  fill="none"
                >
                  <path
                    d="M5 10L1 5.5L5 1"
                    stroke="#FFCC00"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              variant="secondaryContained"
            />
            <h2 className="p-2 font-semibold md:p-0 text-white">
              <span className="text-[#99a3aa]">Logistics /</span> New Logistics
            </h2>
          </div>
        }
        rightContent={
          <Button
            onClick={onSaveClick}
            disabled={loading || selectedVehDriverList?.length === 0}
            startIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            }
            variant="primaryContained"
          >
            {loading ? 'Saving...' : 'Send Consignment'}
          </Button>
        }
      />

      <div className="">
        <div className="mt-5 bg-white rounded-lg shadow-md border border-gray-200 p-6">
          {/* Request Details Section */}
          <div className="border rounded p-4 mb-6">
            <div className="text-left font-bold text-lg mb-2">
              Request Details
            </div>
            <div className="text-left text-sm text-gray-600 mb-4">
              Based on the request please allot the vehicle and driver for the
              trip
            </div>

            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="historicalCheck"
                checked={historicalCheck}
                onChange={(e) => setHistoricalCheck(e.target.checked)}
                className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
              />
              <label
                htmlFor="historicalCheck"
                className="text-sm font-medium text-gray-700"
              >
                Historical Data
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Location *
                </label>
                <InfiniteScrollDropdown
                  fetchFunction={fetchFromWarehouses}
                  queryKey={['inventory-room-dropdown-menu-from', toSite?.value]}
                  transformOption={transformWarehouseOption}
                  onFetchError={() =>
                    handleFetchError('Failed to fetch warehouses')
                  }
                  placeholder="Select From Site"
                  value={fromSite || ''}
                  onSelect={(selected) => setFromSite(selected)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Location *
                </label>
                <InfiniteScrollDropdown
                  fetchFunction={fetchToWarehouses}
                  queryKey={['inventory-room-dropdown-menu-to', fromSite?.value]}
                  transformOption={transformWarehouseOption}
                  onFetchError={() =>
                    handleFetchError('Failed to fetch warehouses')
                  }
                  placeholder="Select To Site"
                  value={toSite || ''}
                  onSelect={(selected) => setToSite(selected)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <div className="text-gray-600 font-medium">NEW</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Material Indents
                </label>
                <div className="w-full">
                  <Input
                    placeholder="Type MI ID and press Enter"
                    value={miInput}
                    onChange={(e) => setMiInput(e.target.value)}
                    onKeyDown={onMiInputKeyDown}
                    disabled={miIDs?.length > 0}
                    parentClassName="mb-0"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {miIDs?.map((it) => (
                      <span
                        key={String(it.value)}
                        className="inline-flex items-center gap-2 px-3 py-1 border border-orange-400 text-gray-900 rounded"
                      >
                        <span>{it.value}</span>
                        <button
                          type="button"
                          className="text-gray-600 hover:text-gray-800"
                          aria-label={`Remove ${it.value}`}
                          onClick={() => removeMiId(it.value)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Materials & Quantities Section */}
          <div className="mb-6">
            <div className="text-left font-bold text-lg mb-4">
              Materials & Quantities Details
            </div>

            {materialsRequested?.map((item, idx) => (
              <div
                className="flex items-end flex-wrap gap-4 border-b pb-4 mb-4"
                key={idx}
              >
                <MaterialSelector
                  category={
                    materialsRequested[idx]?.category
                      ? {
                        value:
                          materialsRequested[idx]?.category?.value ||
                          materialsRequested[idx]?.category,
                        label: materialsRequested[idx]?.category?.label || '',
                      }
                      : null
                  }
                  subCategory={
                    materialsRequested[idx]?.subCatId
                      ? {
                        value: materialsRequested[idx]?.subCatId,
                        label:
                          subCategories?.[idx]?.find(
                            (s) =>
                              s.inventoryDetailsParentId ==
                              materialsRequested[idx]?.subCatId
                          )?.inventoryParentName || '',
                      }
                      : null
                  }
                  material={
                    materialsRequested[idx]?.inventoryDetailsId
                      ? {
                        value: materialsRequested[idx]?.inventoryDetailsId,
                        label:
                          inventoryDetailsList?.[idx]?.find(
                            (i) =>
                              i.inventoryDetailsId ==
                              materialsRequested[idx]?.inventoryDetailsId
                          )?.inventoryName || '',
                      }
                      : null
                  }
                  onSelectMaterial={(selected) => {
                    const newArr = [...materialsRequested];

                    newArr[idx] = {
                      ...newArr[idx],
                      category: selected?.category || null,
                      subCatId: selected?.subCategory?.value || '',
                      inventoryDetailsId: selected?.material?.value || '',
                      quantity: newArr[idx].quantity || 0,
                      measurementUnit:
                        selected?.material?.measurementUnit ||
                        newArr[idx].measurementUnit ||
                        '',
                    };

                    // update subCategories list for this index
                    if (selected?.subCategory) {
                      const availableSubcats = [
                        {
                          inventoryDetailsParentId:
                            selected?.subCategory?.value,
                          inventoryParentName: selected?.subCategory?.label,
                        },
                      ];
                      const newSub = [...subCategories];
                      newSub[idx] = availableSubcats;
                      setSubCategories(newSub);
                    }

                    // update materials list for this index
                    if (selected?.material) {
                      const availableInv = [
                        {
                          inventoryDetailsId: selected?.material?.value,
                          inventoryName: selected?.material?.label,
                          measurementUnit: selected?.material?.measurementUnit,
                        },
                      ];
                      const newInv = [...inventoryDetailsList];
                      newInv[idx] = availableInv;
                      setInventoryDetailsList(newInv);
                    }

                    setMaterialsRequested(newArr);
                  }}
                  errors={null}
                  className="min-w-[300px]"
                />
                <div>
                  <div className="relative">
                    <Input
                      label="Quantity"
                      type="number"
                      value={materialsRequested[idx]?.quantity || ''}
                      onChange={(e) => onQuantityUpdate(e, idx)}
                      placeholder="0"
                      parentClassName="mb-0 w-[100px]"
                    />
                    {materialsRequested[idx]?.measurementUnit && (
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 mt-4">
                        {materialsRequested[idx].measurementUnit}
                      </span>
                    )}
                  </div>
                </div>

                {materialsRequested?.length !== 1 && (
                  <button
                    onClick={() => onDeleteRow(idx)}
                    className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}

                {materialsRequested?.length === idx + 1 && (
                  <button
                    onClick={onAddRow}
                    disabled={miIDs?.length > 0}
                    className={`px-4 py-2 rounded text-white ${miIDs?.length > 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                      }`}
                  >
                    + Add More
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Transport Details Section */}
          <div className="mb-6">
            <div className="text-left font-bold text-lg mb-4">
              Transport Details
            </div>
            <div className="text-gray-600 mb-4">
              Only Available Vehicles and drivers are shown below
            </div>

            {/* <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4 max-w-2xl">
              <div className="font-bold text-yellow-800">Note</div>
              <div className="text-yellow-700 text-sm mt-2">
                If the requested quantity exceeds the vehicle capacity add
                additional vehicle to avoid over load.
              </div>
            </div> */}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Mode:
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  id="existingMode"
                  name="selectionMode"
                  checked={vehicleDriverSelectionMode === 'existing'}
                  onChange={() => setVehicleDriverSelectionMode('existing')}
                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500"
                />
                <label
                  htmlFor="existingMode"
                  className="text-sm font-medium text-gray-700"
                >
                  Existing Details
                </label>

                <input
                  type="radio"
                  id="customMode"
                  name="selectionMode"
                  checked={vehicleDriverSelectionMode === 'custom'}
                  onChange={() => setVehicleDriverSelectionMode('custom')}
                  className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 ml-4"
                />
                <label
                  htmlFor="customMode"
                  className="text-sm font-medium text-gray-700"
                >
                  Custom Details
                </label>
              </div>
            </div>

            {vehicleDriverSelectionMode === 'existing' && (
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <Input
                    label="Start Date"
                    type="date"
                    value={selectedStart.toISOString().split('T')[0]}
                    onChange={(e) => onStartChange(new Date(e.target.value))}
                    parentClassName="mb-0"
                    // min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="flex-1">
                  <Input
                    type="date"
                    label="End Date"
                    value={selectedEnd.toISOString().split('T')[0]}
                    onChange={(e) => onEndChange(new Date(e.target.value))}
                    parentClassName="mb-0"
                    // min={selectedStart.toISOString().split('T')[0]}
                  />
                </div>

                <div className="flex-1">
                  <DropdownMenu
                    label="Vehicle"
                    placeholder="Select Vehicle"
                    options={
                      vehiclesList?.map((vehicle, index) => ({
                        value: index.toString(),
                        label: vehicle.name,
                      })) || []
                    }
                    value={selectedVehicleOption}
                    onSelect={(selected) =>
                      setSelectedVehicleOption(selected?.value)
                    }
                    localSearch
                  />
                  {/* <InfiniteScrollDropdown
                    fetchFunction={fetchCategories}
                    queryKey="categories-dropdown-menu"
                    transformOption={transformCategoryOption}
                    onFetchError={() =>
                      handleFetchError('Failed to fetch categories')
                    }
                    placeholder="Select Category"
                    value={materialsRequested[idx]?.category || ''}
                    onSelect={(selected) => onCategoryChange(selected, idx)}
                    label="Category"
                  /> */}
                </div>

                <div className="flex-1">
                  <DropdownMenu
                    label="Driver"
                    placeholder="Select Driver"
                    options={
                      driversList?.map((driver, index) => ({
                        value: index.toString(),
                        label: driver.name,
                      })) || []
                    }
                    value={selectedDriverOption}
                    onSelect={(selected) =>
                      setSelectedDriverOption(selected?.value)
                    }
                    localSearch
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={onAddVehicleDriver}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {vehicleDriverSelectionMode === 'custom' && (
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Vehicle Name
                  </label>
                  <Input
                    type="text"
                    value={customVehicleName}
                    onChange={(e) => setCustomVehicleName(e.target.value)}
                    placeholder="Enter Vehicle Name"
                    parentClassName="mb-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Driver Name
                  </label>
                  <Input
                    type="text"
                    value={customDriverName}
                    onChange={(e) => setCustomDriverName(e.target.value)}
                    placeholder="Enter Driver Name"
                    parentClassName="mb-0"
                  />
                </div>

                <div className="flex items-end">
                  <button
                    onClick={onAddCustomVehicleDriver}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Add Custom
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Dispatch Preview Section */}
          <div>
            <div className="text-left font-bold text-lg mb-4">
              Dispatch Preview
            </div>

            {selectedVehDriverList.map((item, index) => (
              <div
                className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4"
                key={index}
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-gray-600 font-medium mb-2">
                      Vehicle Details
                    </div>
                    <div className="text-sm">
                      <div>
                        <b>Name:</b>{' '}
                        {item?.isCustom ? item?.vehicle : item?.vehicle?.name}
                      </div>
                      {!item?.isCustom && (
                        <>
                          <div>
                            <b>Number:</b> {item?.vehicle?.number}
                          </div>
                          <div>
                            <b>Capacity:</b> {item?.vehicle?.totalCapacity}{' '}
                            {item?.vehicle?.unityType}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-600 font-medium mb-2">
                      Driver Details
                    </div>
                    <div className="text-sm">
                      <div>
                        <b>Name:</b>{' '}
                        {item?.isCustom ? item?.driver : item?.driver?.name}
                      </div>
                      {!item?.isCustom && (
                        <div>
                          <b>Mobile:</b> {item?.driver?.mobile}
                        </div>
                      )}
                    </div>
                  </div>

                  {!item?.isCustom && (
                    <div>
                      <div className="text-gray-600 font-medium mb-2">
                        Schedule
                      </div>
                      <div className="text-sm">
                        <div>
                          <b>Start:</b> {item?.start?.toDateString()}
                        </div>
                        <div>
                          <b>End:</b> {item?.end?.toDateString()}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center">
                    <button
                      onClick={() => onRemoveVehicleDriver(item, index)}
                      className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {selectedVehDriverList.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                No vehicles and drivers added yet. Please add at least one
                vehicle-driver combination.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
